// Modal pra escolher um pacote e gerar o QR Pix. Após a compra criada,
// mostra o BRCode renderizado + copia-e-cola.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { Switch } from "@app/ui/components/switch";
import { generateQrWithLogoSvg } from "@app/ui/lib/qr-with-logo";
import {
  AlertCircle,
  Check,
  Copy,
  CreditCard,
  Loader2,
  Package,
  QrCode,
  ShoppingBag,
  X
} from "lucide-react";
import {
  BillingAddressFields,
  emptyBillingAddress,
  validateBillingAddress as validateAddrClient,
  type BillingAddress
} from "@app/ui/components/billing-address-fields";
import { api, ApiError, type CompraRow, type PacoteAvailable } from "./api";
import { data } from "./App";

interface OpenArgs {
  disciplinaId: string;
  disciplinaName: string;
  offeringId: string;
}

const open = signal<OpenArgs | null>(null);
const pacotes = signal<PacoteAvailable[]>([]);
const phase = signal<"choosing" | "creating" | "showing-pix" | "error">("choosing");
const errMsg = signal<string | null>(null);
const created = signal<CompraRow | null>(null);
// Quando a compra criou também uma assinatura recorrente, guardamos o id +
// data da próxima cobrança pra:
//   1. Mostrar no success screen ("Próxima cobrança em D/M/AAAA")
//   2. Passar pro pay-cc se o aluno escolher trocar pra cartão de crédito
const createdSubscription = signal<{ id: string; nextChargeAt: string } | null>(
  null
);

export function openCompraPacote(args: OpenArgs) {
  open.value = args;
  phase.value = "choosing";
  pacotes.value = [];
  created.value = null;
  createdSubscription.value = null;
  errMsg.value = null;
  void loadPacotes(args);
}

async function loadPacotes(args: OpenArgs) {
  try {
    const r = await api.listPacotesDisciplina(args.disciplinaId, args.offeringId);
    pacotes.value = r.pacotes;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = e instanceof Error ? e.message : String(e);
    phase.value = "error";
  }
}

function close() {
  open.value = null;
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function CompraPacoteModal({
  onCompraCreated
}: {
  onCompraCreated?: (compra: CompraRow) => void;
}) {
  const args = open.value;
  if (!args) return null;

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) close();
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div class="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div class="min-w-0 flex-1">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <ShoppingBag className="size-4 text-primary" />
              {phase.value === "showing-pix" ? "Pagamento" : "Comprar pacote"}
            </h2>
            <p class="truncate text-[11px] text-muted-foreground">
              {args.disciplinaName}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto p-5">
          {phase.value === "error" && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
              <p class="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="size-4" />
                {errMsg.value}
              </p>
            </div>
          )}

          {phase.value === "choosing" && (
            <ChoosingPhase
              args={args}
              onCreated={(c) => {
                created.value = c;
                phase.value = "showing-pix";
                onCompraCreated?.(c);
              }}
              onError={(msg) => {
                errMsg.value = msg;
                phase.value = "error";
              }}
            />
          )}

          {phase.value === "creating" && (
            <p class="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Gerando pagamento…
            </p>
          )}

          {phase.value === "showing-pix" && created.value && (
            <PixPhase compra={created.value} />
          )}
        </div>
      </div>
    </div>
  );
}

function ChoosingPhase({
  args,
  onCreated,
  onError
}: {
  args: OpenArgs;
  onCreated: (c: CompraRow) => void;
  onError: (msg: string) => void;
}) {
  const list = pacotes.value;
  const [selected, setSelected] = useState<string | null>(null);
  const [recurring, setRecurring] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function comprar() {
    if (!selected) return;
    setSubmitting(true);
    phase.value = "creating";
    try {
      const r = await api.createCompra({
        offeringId: args.offeringId,
        pacoteId: selected,
        recurring
      });
      if (r.subscriptionId && r.nextChargeAt) {
        createdSubscription.value = {
          id: r.subscriptionId,
          nextChargeAt: r.nextChargeAt
        };
      } else {
        createdSubscription.value = null;
      }
      onCreated(r.compra);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (list.length === 0) {
    return (
      <p class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando pacotes…
      </p>
    );
  }

  return (
    <div class="space-y-3">
      <p class="text-xs text-muted-foreground">
        Escolha um pacote. Pacotes maiores costumam ter melhor preço por aula.
      </p>
      <ul class="space-y-2">
        {list.map((p) => {
          const valorUnit = p.numAulas > 0 ? p.precoCents / p.numAulas : 0;
          const isSelected = selected === p.id;
          return (
            <li key={p.id}>
              <label
                class={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border bg-background/60 hover:border-primary/40"
                }`}
              >
                <input
                  type="radio"
                  name="pacote"
                  checked={isSelected}
                  onChange={() => setSelected(p.id)}
                  class="mt-1 accent-primary"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <Package className="size-3.5 text-primary" />
                    <span class="text-sm font-bold">{p.nome}</span>
                  </div>
                  <div class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                    <span class="font-semibold text-foreground">
                      {p.numAulas} aulas
                    </span>
                    <span>·</span>
                    <span class="font-semibold text-foreground">
                      {formatBRL(p.precoCents)}
                    </span>
                    <span class="opacity-50">
                      ({formatBRL(valorUnit)} / aula)
                    </span>
                  </div>
                  {p.descricao && (
                    <p class="mt-1 text-[11px] text-muted-foreground">
                      {p.descricao}
                    </p>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      {/* Recorrência opcional — toda compra começa avulsa (default false). */}
      <label
        class={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors ${
          recurring
            ? "border-primary bg-primary/5"
            : "border-border bg-background/60 hover:border-primary/40"
        }`}
      >
        <Switch
          checked={recurring}
          onCheckedChange={(v) => setRecurring(v)}
          disabled={submitting}
          className="mt-1"
        />
        <div class="min-w-0 flex-1">
          <div class="text-sm font-bold">Cobrança recorrente automática</div>
          <p class="mt-0.5 text-[11px] text-muted-foreground">
            Todo mês a partir de hoje, você recebe automaticamente um novo
            Pix (ou cobramos no cartão se você escolher cartão na próxima
            tela). Pode cancelar a qualquer momento em "Forma de pagamento".
          </p>
        </div>
      </label>

      <div class="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={close}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={comprar}
          disabled={!selected || submitting}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {submitting && <Loader2 className="size-3.5 animate-spin" />}
          Pagar
        </button>
      </div>
    </div>
  );
}

function PixPhase({ compra }: { compra: CompraRow }) {
  return <PixDisplay compra={compra} />;
}

// Aceita tipos de pagamento que o aluno pode escolher quando há uma compra
// pendente já criada (com Pix gerado). CC abre formulário inline.
type PayMethod = "pix" | "cc";

export function PixDisplay({ compra }: { compra: CompraRow }) {
  const methods = data.value?.paymentMethods;
  const ccEnabled = methods?.ccEnabled ?? false;
  const pixEnabled = methods?.pixEnabled ?? true;
  const sub = createdSubscription.value;
  const [selected, setSelected] = useState<PayMethod>(pixEnabled ? "pix" : "cc");
  const [qrSvg, setQrSvg] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    generateQrWithLogoSvg(compra.pixBrcode, {
      margin: 1,
      dark: "#0f172a",
      light: "#ffffff"
    })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, [compra.pixBrcode]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(compra.pixBrcode);
    } catch {
      // ignora
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div class="space-y-3">
      <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-center">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
          {compra.pacoteNomeSnap}
        </p>
        <p class="mt-1 text-2xl font-bold">
          {(compra.valorCents / 100).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL"
          })}
        </p>
        <p class="text-[11px] text-muted-foreground">
          {compra.numAulasSnap} aulas
        </p>
        {sub && (
          <p class="mt-2 inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300">
            Recorrente — próxima cobrança em{" "}
            {new Date(sub.nextChargeAt).toLocaleDateString("pt-BR")}
          </p>
        )}
      </div>

      {/* Tabs: só aparecem se mais de um método estiver habilitado */}
      {pixEnabled && ccEnabled && (
        <div class="grid grid-cols-2 gap-2 rounded-md border border-border bg-background p-1">
          <MethodTab
            active={selected === "pix"}
            icon={QrCode}
            label="Pix"
            onClick={() => setSelected("pix")}
          />
          <MethodTab
            active={selected === "cc"}
            icon={CreditCard}
            label="Cartão"
            onClick={() => setSelected("cc")}
          />
        </div>
      )}

      {selected === "pix" && pixEnabled && (
        <>
          {qrSvg ? (
            <div
              class="mx-auto size-[240px] overflow-hidden rounded-md bg-white p-3 shadow-sm [&_svg]:size-full"
              dangerouslySetInnerHTML={{ __html: qrSvg }}
              aria-label="QR Code Pix"
            />
          ) : (
            <div class="mx-auto grid size-[240px] place-items-center rounded-md bg-muted text-xs text-muted-foreground">
              Gerando QR…
            </div>
          )}

          <div>
            <div class="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Pix copia e cola
            </div>
            <div class="flex items-center gap-1 rounded-md border border-border bg-background">
              <code class="min-w-0 flex-1 truncate px-2 py-1.5 font-mono text-[10px]">
                {compra.pixBrcode}
              </code>
              <button
                type="button"
                onClick={copy}
                title={copied ? "Copiado!" : "Copiar"}
                aria-label="Copiar Pix copia e cola"
                class={`grid size-8 shrink-0 place-items-center rounded-r-md border-l border-border transition-colors ${
                  copied
                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              </button>
            </div>
          </div>

          <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-800 dark:text-amber-300">
            Depois de pagar, a organização confirma manualmente o recebimento e
            seus créditos são liberados. Você receberá uma notificação.
          </div>
        </>
      )}

      {selected === "cc" && ccEnabled && <CompraCardForm compra={compra} />}
    </div>
  );
}

function MethodTab({
  active,
  icon: Icon,
  label,
  onClick
}: {
  active: boolean;
  icon: typeof QrCode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

// Formulário inline pra pagar a compra existente com cartão de crédito.
// Coleta todos os dados que um gateway brasileiro exige pra cobrar online:
// número, nome, CPF do portador, CVV, validade.
function CompraCardForm({ compra }: { compra: CompraRow }) {
  const [number, setNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [holderDoc, setHolderDoc] = useState("");
  const [cvv, setCvv] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [address, setAddress] = useState<BillingAddress>(emptyBillingAddress);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [paidCard, setPaidCard] = useState<{ brand: string; last4: string } | null>(
    null
  );

  async function pay(e: Event) {
    e.preventDefault();
    const addrErr = validateAddrClient(address);
    if (addrErr) {
      setErrMsg(addrErr);
      return;
    }
    setSubmitting(true);
    setErrMsg(null);
    try {
      const r = await api.payCompraWithCC(compra.id, {
        number,
        holderName,
        holderDoc,
        cvv,
        expMonth: parseInt(expMonth, 10),
        expYear: parseInt(expYear, 10),
        billingAddress: address,
        subscriptionId: createdSubscription.value?.id
      });
      setPaidCard(r.card);
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (paidCard) {
    return (
      <div class="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-4 text-center">
        <Check className="mx-auto mb-2 size-8 text-emerald-600 dark:text-emerald-400" />
        <p class="text-sm font-bold text-emerald-800 dark:text-emerald-300">
          Pagamento aprovado!
        </p>
        <p class="mt-1 text-xs text-muted-foreground">
          {paidCard.brand} •••• {paidCard.last4}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={pay} class="space-y-3">
      {/* Dados do cartão */}
      <fieldset class="space-y-2">
        <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Dados do cartão
        </legend>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Número do cartão
          </label>
          <input
            type="text"
            inputMode="numeric"
            required
            minLength={12}
            maxLength={19}
            value={number}
            onInput={(e) => setNumber((e.target as HTMLInputElement).value)}
            placeholder="4111 1111 1111 1111"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={submitting}
          />
        </div>
        <div class="grid grid-cols-3 gap-2">
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Mês
            </label>
            <input
              type="number"
              min={1}
              max={12}
              required
              value={expMonth}
              onInput={(e) => setExpMonth((e.target as HTMLInputElement).value)}
              placeholder="MM"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Ano
            </label>
            <input
              type="number"
              min={2025}
              max={2099}
              required
              value={expYear}
              onInput={(e) => setExpYear((e.target as HTMLInputElement).value)}
              placeholder="AAAA"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              CVV
            </label>
            <input
              type="text"
              inputMode="numeric"
              required
              minLength={3}
              maxLength={4}
              value={cvv}
              onInput={(e) => setCvv((e.target as HTMLInputElement).value)}
              placeholder="123"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
        </div>
      </fieldset>

      {/* Dados do portador */}
      <fieldset class="space-y-2">
        <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Dados do portador
        </legend>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Nome do portador (como está no cartão)
          </label>
          <input
            type="text"
            required
            value={holderName}
            onInput={(e) => setHolderName((e.target as HTMLInputElement).value)}
            placeholder="NOME COMO IMPRESSO"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase"
            disabled={submitting}
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            CPF do portador
          </label>
          <input
            type="text"
            inputMode="numeric"
            required
            value={holderDoc}
            onInput={(e) => setHolderDoc((e.target as HTMLInputElement).value)}
            placeholder="000.000.000-00"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={submitting}
          />
        </div>
      </fieldset>

      {/* Endereço de cobrança */}
      <fieldset class="space-y-2">
        <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Endereço de cobrança
        </legend>
        <BillingAddressFields
          value={address}
          onChange={setAddress}
          disabled={submitting}
        />
      </fieldset>

      {errMsg && (
        <div class="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mr-1 inline size-3" />
          {errMsg}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
      >
        {submitting ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <CreditCard className="size-4" />
        )}
        Pagar agora
      </button>
    </form>
  );
}
